import { AwesomeEvent } from './component';
import { getList } from './component/awesome-event';
import EventData from './component/event/event-data';
import EventDelegator from './component/event/event-delegator';
import { configureEventListener } from './component/event/event-listener';
import Mountable, { MountEventListener } from './component/types/mountable';
import { AuthenticationService, SessionStorageService } from './services';
import SingleSourceOfTruth, { StateData } from './single-source-of-truth';

const _eventDelegator = Symbol('eventDelegator');
const _ssot = Symbol('ssot');

const LIFE_CYCLE_EVENTS = ['onMount', 'onUnmount'];

function configure(instance: ComponentAPI, ssot: SingleSourceOfTruth, eventDelegator: EventDelegator<any, any>): void {

  if (instance[_ssot] || instance[_eventDelegator]) {
    throw new Error('Component is already configured.');
  }

  instance[_ssot] = ssot;
  instance[_eventDelegator] = eventDelegator;

  const eventList = getList(instance);
  eventList.concat(LIFE_CYCLE_EVENTS).forEach(type => {
    const eventListener = instance[type];
    configureEventListener(eventListener, { type, eventDelegator, api: instance });
  });
}

class ComponentAPI implements Mountable {

  /** @ignore **/
  [_eventDelegator]: EventDelegator<ComponentAPI, EventData<ComponentAPI>>;
  /** @ignore **/
  [_ssot]: SingleSourceOfTruth;
  /** @ignore **/
  [key: string]: any;

  /** @event */
  @AwesomeEvent
  onMount = new MountEventListener();

  //@AwesomeEvent
  //onRender = new MountEventListener();

  /** @event */
  @AwesomeEvent
  onUnmount = new MountEventListener();

  setState(data: {}): void {
    const ssot = this[_ssot];
    ssot.applyAndNotify(data);
  }

  get state(): StateData {
    const ssot = this[_ssot];
    return ssot.state;
  }

  setClassName(className: string): void {
    this.setState({ className });
  }

  getClassName(): string {
    return this.state.className;
  }

  persistState(ignoreAuthentication: boolean = false): void {
    if (!AuthenticationService.isAuthenticated() && !ignoreAuthentication) {
      console.warn(
        `[Component API]: Trying to persist the component state while not authenticated. If this is the correct behavior, please use the 'ignoreAuthentication' flag.`
      );

      return;
    }

    const state = { ...this[_ssot].props, ...this[_ssot].volatileState };

    SessionStorageService.setItem(this[_ssot].hashCode, state);
  }

  restoreState(clear: boolean = false): boolean {
    const state = SessionStorageService.getItem(this[_ssot].hashCode);

    if (state) {
      this.setState(state);

      clear && this.clearPersistedState();

      return true;
    }

    return false;
  }

  clearPersistedState(): void {
    SessionStorageService.removeItem(this[_ssot].hashCode);
  }

  discard(): void {
    // ??? ¯\_(ツ)_/¯
  }

  setFormatterByProperty(property: string, name: string, options?: object): void {
    const { formatters = {} } = this.state;
    const formatter = {
      name,
      options,
    };

    this.setState({ formatters: { ...formatters, [property]: formatter } });
  }

  getFormatterByProperty(property: string): string {
    const { formatters = {} }  = this.state;

    return formatters[property];
  }

  getFormatters(): object {
    return this.state.formatters;
  }

  removeFormatterByProperty(name: string): void {
    const { formatters = {} } = this.state;
    const newFormatters = { ...formatters };
    delete newFormatters[name];
    this.setState({ formatters: newFormatters });
  }
}

function get(instance: ComponentAPI): { ssot: SingleSourceOfTruth; eventDelegator: EventDelegator<ComponentAPI, EventData<ComponentAPI>> } {
  const ssot = instance[_ssot];
  const eventDelegator = instance[_eventDelegator];

  return { ssot, eventDelegator };
}

export { ComponentAPI as default, get, configure };
